// Copyright 2024 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    CriticalBandSpectrumSet,
    GenericMeasurement,
    MetricPsychoAcousticsProperties,
    PsychoAcousticsAnalysisProperties,
    PsychoAcousticsBandsResults,
    PsychoAcousticsOverTimeResults,
    PsychoAcousticsResults,
} from "datatypes/SoramaWebApi";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { CacheEntry, CacheKey, IPsychoAcousticsCacheService } from "./IPsychoAcousticsCacheService";

/**
 * Singleton service that provides caching functionality for psychoacoustics calculations
 * to avoid redundant API calls between modal transitions
 */
@autoinject
export class PsychoAcousticsCacheService implements IPsychoAcousticsCacheService {
    /**
     * Time in milliseconds after which cached entries are considered expired
     * Default is 30 minutes
     */
    private static CACHE_EXPIRATION = 30 * 60 * 1000;

    // Add constant for max sessionStorage size (in bytes)
    private static readonly MAX_SESSION_SIZE = 5 * 1024 * 1024; // 5MB

    /**
     * Storage for different types of cached results
     * Uses Map for O(1) lookup performance.
     */
    public psychoAcousticsCache: Map<string, CacheEntry<PsychoAcousticsResults>> = new Map();
    private metricOverTimeCache: Map<string, CacheEntry<PsychoAcousticsOverTimeResults>> = new Map();
    private metricBandsCache: Map<string, CacheEntry<PsychoAcousticsBandsResults>> = new Map();
    private criticalBandSetCache: Map<string, CacheEntry<CriticalBandSpectrumSet>> = new Map();

    /**
     * Generates a unique cache key from a measurement and its analysis properties.
     * The microphone coordinates are rounded to two decimals.
     * @param measurement The measurement object.
     * @param properties The psychoacoustics analysis properties.
     * @param metricType Optional metric type.
     * @returns A stringified cache key.
     */
    public generateCacheKey(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties,
        metricType?: string
    ): string {
        try {
            const requestedMicrophone = (<PsychoAcousticsAnalysisProperties>properties).requestedMicrophone;
            // Round the microphone coordinates to 2 decimals
            const roundedMicrophone = {
                x: parseFloat(requestedMicrophone.x.toFixed(2)),
                y: parseFloat(requestedMicrophone.y.toFixed(2)),
                z: parseFloat(requestedMicrophone.z.toFixed(2)),
            };
            const key: CacheKey = {
                measurementId: measurement.id,
                propertiesHash: JSON.stringify(roundedMicrophone),
                metricType: metricType,
            };
            return JSON.stringify(key);
        } catch (error) {
            console.warn("Failed to generate cache key:", error);
            // Fallback to a simpler key
            return `${measurement.id}-${Date.now()}`;
        }
    }

    /**
     * Checks whether a cached entry is expired based on its timestamp.
     * @param timestamp The cached entry timestamp.
     * @returns True if expired; otherwise false.
     */
    private isExpired(timestamp: number): boolean {
        try {
            return Date.now() - timestamp > PsychoAcousticsCacheService.CACHE_EXPIRATION;
        } catch (error) {
            console.warn("Error checking cache expiration:", error);
            return true; // Safer to consider it expired if error occurs
        }
    }

    /**
     * Computes an approximate total size of sessionStorage content.
     * @returns The total size (in characters) of keys and values.
     */
    private getSessionStorageSize(): number {
        let size = 0;
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            const value = sessionStorage.getItem(key);
            size += (key ? key.length : 0) + (value ? value.length : 0);
        }
        return size;
    }

    /**
     * Removes the oldest cached entry from sessionStorage based on its timestamp.
     */
    private removeOldestSessionEntry(): void {
        let oldestKey: string | null = null;
        let oldestTimestamp = Infinity;
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
                try {
                    const cacheEntry = JSON.parse(sessionStorage.getItem(key)!);
                    if (cacheEntry.timestamp && cacheEntry.timestamp < oldestTimestamp) {
                        oldestTimestamp = cacheEntry.timestamp;
                        oldestKey = key;
                    }
                } catch (error) {
                    // Skip keys that are not cache entries
                }
            }
        }
        if (oldestKey) {
            sessionStorage.removeItem(oldestKey);
        }
    }

    /**
     * Caches the psychoacoustics results for a measurement.
     * Ensures there is sufficient space in sessionStorage (up to MAX_SESSION_SIZE)
     * by removing the oldest entries if necessary.
     * @param measurement The measurement object.
     * @param properties The analysis properties.
     * @param results The psychoacoustics results to cache.
     */
    public cachePsychoAcoustics(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties,
        results: PsychoAcousticsResults
    ): void {
        try {
            const key = this.generateCacheKey(measurement, properties);
            const cacheEntry = {
                data: results,
                timestamp: Date.now(),
                properties: properties,
            };
            // Ensure there is space in sessionStorage
            while (this.getSessionStorageSize() > PsychoAcousticsCacheService.MAX_SESSION_SIZE) {
                this.removeOldestSessionEntry();
            }
            // Store in memory
            this.psychoAcousticsCache.set(key, cacheEntry);
            // Store in sessionStorage as well
            sessionStorage.setItem(key, JSON.stringify(cacheEntry));
        } catch (error) {
            console.warn("Failed to cache psychoacoustics results:", error);
        }
    }

    /**
     * Retrieves cached psychoacoustics results for the given measurement and properties.
     * Checks sessionStorage first; if not found or expired, returns null.
     * @param measurement The measurement object.
     * @param properties The analysis properties.
     * @returns The cached results, or null if unavailable or expired.
     */
    public getCachedPsychoAcoustics(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties
    ): PsychoAcousticsResults | null {
        try {
            const key = this.generateCacheKey(measurement, properties);

            // First try to retrieve from sessionStorage
            const sessionCache = sessionStorage.getItem(key);
            if (sessionCache) {
                const cached = JSON.parse(sessionCache);
                if (!this.isExpired(cached.timestamp)) {
                    return cached.data;
                } else {
                    sessionStorage.removeItem(key); // Remove expired entry
                }
            }

            // If not in sessionStorage, check in-memory cache
            const cachedInMemory = this.psychoAcousticsCache.get(key);
            if (cachedInMemory && !this.isExpired(cachedInMemory.timestamp)) {
                return cachedInMemory.data;
            }

            return null;
        } catch (error) {
            console.warn("Error retrieving from cache:", error);
        }
        return null;
    }

    /**
     * Caches psychoacoustics "over time" metric results.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @param results The over time results.
     */
    public cacheMetricOverTime(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        results: PsychoAcousticsOverTimeResults
    ): void {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            this.metricOverTimeCache.set(key, {
                data: results,
                timestamp: Date.now(),
                properties: properties,
            });
        } catch (error) {
            console.warn("Failed to cache metric over time results:", error);
        }
    }

    /**
     * Retrieves cached "over time" metric results for a measurement.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @returns The cached over time results, or null if unavailable or expired.
     */
    public getCachedMetricOverTime(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties
    ): PsychoAcousticsOverTimeResults | null {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            const cached = this.metricOverTimeCache.get(key);

            if (cached && !this.isExpired(cached.timestamp)) {
                return cached.data;
            }

            if (cached) {
                this.metricOverTimeCache.delete(key);
            }
        } catch (error) {
            console.warn("Error retrieving from cache:", error);
        }
        return null;
    }

    /**
     * Caches metric bands results for a measurement.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @param results The bands results to cache.
     */
    public cacheMetricBands(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        results: PsychoAcousticsBandsResults
    ): void {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            this.metricBandsCache.set(key, {
                data: results,
                timestamp: Date.now(),
                properties: properties,
            });
        } catch (error) {
            console.warn("Failed to cache metric bands results:", error);
        }
    }

    /**
     * Retrieves cached metric bands results for a measurement.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @returns The cached bands results, or null if unavailable or expired.
     */
    public getCachedMetricBands(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties
    ): PsychoAcousticsBandsResults | null {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            const cached = this.metricBandsCache.get(key);

            if (cached && !this.isExpired(cached.timestamp)) {
                return cached.data;
            }

            if (cached) {
                this.metricBandsCache.delete(key);
            }
        } catch (error) {
            console.warn("Error retrieving from cache:", error);
        }
        return null;
    }

    /**
     * Caches a critical band spectrum set for a measurement.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @param results The critical band spectrum set to cache.
     */
    public cacheCriticalBandSet(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        results: CriticalBandSpectrumSet
    ): void {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            this.criticalBandSetCache.set(key, {
                data: ObjectExtensions.copy(results),
                timestamp: Date.now(),
                properties: properties,
            });
        } catch (error) {
            console.warn("Failed to cache critical band set:", error);
        }
    }

    /**
     * Retrieves a cached critical band spectrum set for a measurement.
     * @param measurement The measurement object.
     * @param properties The metric psychoacoustics properties.
     * @returns The cached critical band spectrum set, or null if unavailable or expired.
     */
    public getCachedCriticalBandSet(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties
    ): CriticalBandSpectrumSet | null {
        try {
            const key = this.generateCacheKey(measurement, properties, properties.metricName);
            const cached = this.criticalBandSetCache.get(key);

            if (cached && !this.isExpired(cached.timestamp)) {
                return cached.data;
            }

            if (cached) {
                this.criticalBandSetCache.delete(key);
            }
        } catch (error) {
            console.warn("Error retrieving from cache:", error);
        }
        return null;
    }

    /**
     * Clears all cached data from in-memory caches and sessionStorage.
     */
    public clearCache(): void {
        // Clear in-memory cache
        this.psychoAcousticsCache.clear();
        this.metricOverTimeCache.clear();
        this.metricBandsCache.clear();
        this.criticalBandSetCache.clear();

        // Clear sessionStorage as well
        sessionStorage.clear();
    }
}
